import { useInfiniteScroll } from "@vueuse/core";
import type { Ref } from "vue";
import { nextTick, ref, shallowRef } from "vue";

export const SELECT_MENU_LOADING_ROW_ID = "__loading_skeleton__";

const LOAD_MORE_DISTANCE_PX = 50;

export type SelectMenuRef = { getOptionsViewport: () => HTMLElement | null };

export const useSelectMenuLoadMore = <T extends { id: string }>(params: {
  pageSize: number;
  selectMenuRef: Ref<SelectMenuRef | null>;
  loadPage: (pageIndex: number, loadedItems: T[]) => Promise<T[]>;
}) => {
  const { pageSize, selectMenuRef, loadPage } = params;

  const items = shallowRef<T[]>([]);
  const isFirstLoad = ref(false);
  const isRefreshing = ref(false);
  const isLoadingMore = ref(false);
  const hasMore = ref(true);
  const isInitialized = ref(false);
  let currentPageIndex = 0;

  const getOptionsViewport = (): HTMLElement | null => selectMenuRef.value?.getOptionsViewport() ?? null;

  const canLoadMoreNow = () =>
    isInitialized.value && hasMore.value && !isLoadingMore.value && !isRefreshing.value && !isFirstLoad.value;

  const isNearBottom = (viewport: HTMLElement) => {
    const distanceFromBottom = viewport.scrollHeight - viewport.scrollTop - viewport.clientHeight;
    return distanceFromBottom <= LOAD_MORE_DISTANCE_PX;
  };

  const loadFirstPage = async () => {
    items.value = [];
    currentPageIndex = 0;
    hasMore.value = true;

    const firstPage = await loadPage(0, []);
    items.value = firstPage;
    hasMore.value = firstPage.length === pageSize;
  };

  const initialize = async (prepare?: () => Promise<void>) => {
    isFirstLoad.value = true;

    try {
      await prepare?.();
      await loadFirstPage();
      isInitialized.value = true;
    } finally {
      isFirstLoad.value = false;
      await checkAndLoadMoreIfNeeded();
    }
  };

  const refresh = async () => {
    if (!isInitialized.value) return;

    isRefreshing.value = true;

    try {
      await loadFirstPage();
    } finally {
      isRefreshing.value = false;
      await checkAndLoadMoreIfNeeded();
    }
  };

  const loadNextPage = async () => {
    if (isLoadingMore.value || !hasMore.value || isRefreshing.value || isFirstLoad.value) return;

    isLoadingMore.value = true;

    const viewport = getOptionsViewport();
    const scrollTop = viewport?.scrollTop ?? 0;

    try {
      const nextPageIndex = currentPageIndex + 1;
      const nextPage = await loadPage(nextPageIndex, items.value);

      if (nextPage.length > 0) {
        items.value = [...items.value, ...nextPage];
        currentPageIndex = nextPageIndex;
        hasMore.value = nextPage.length === pageSize;
      } else {
        hasMore.value = false;
      }
    } finally {
      isLoadingMore.value = false;

      await nextTick();

      if (viewport) {
        viewport.scrollTop = scrollTop;
      }

      await checkAndLoadMoreIfNeeded();
    }
  };

  const checkAndLoadMoreIfNeeded = async () => {
    await nextTick();

    if (!canLoadMoreNow()) return;

    const viewport = getOptionsViewport();
    if (!viewport) return;

    if (!isNearBottom(viewport)) return;

    await loadNextPage();
  };

  useInfiniteScroll(() => selectMenuRef.value?.getOptionsViewport() ?? null, loadNextPage, {
    distance: LOAD_MORE_DISTANCE_PX,
    canLoadMore: canLoadMoreNow,
  });

  return {
    items,
    isFirstLoad,
    isRefreshing,
    isLoadingMore,
    hasMore,
    isInitialized,
    initialize,
    refresh,
  };
};
